import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";

import {
  TRUE,
  LOGIN_FORM,
  EMPTY_STRING,
  CURRENCY_SYMBOL,
  localStorageKeys,
  eventBookingParams,
  eventBookingConstants,
  eventBookingStatus,
  numberConstants,
} from "../../../../constants";
import { getItemFromStorage } from "../../../../system";
import {
  generateReels,
  generateEvents,
  numberToDecimals,
  isArrayWithItems,
  normalizeAddRecapz,
  calculatePercentage,
  validateEventBooking,
  mergeRecapzWithBackup,
  filterSelectedShutterBsInEditDraftEvents,
} from "../../../../shared";
import Modal from "../../../../container/Views/Login/Modal";
import BackButton from "../../../../shared/components/IconButton/BackButton";
import { eventBookingActions } from "../../../../redux/actions";

import Checkout from "../CartSummary/Checkout";
import CartSummaryTable from "../CartSummary/CartSummaryTable";

const CartSummary = (props) => {
  const { isBookingStatus, eventGroupId } = useParams();

  const isDraftEvent = isBookingStatus === eventBookingParams.DRAFT_EVENT;

  const totalFeeBackupRef = useRef(0);
  const initialTotalFeeRef = useRef(0);

  const [totalFee, setTotalFee] = useState(0);
  const [totalTip, setTotalTip] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [serviceFee, setServiceFee] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [taxPercentValue, setTaxPercentvalue] = useState(0);
  const [remainingBalance, setRemainingBalance] = useState(0);
  const [alreadyPaidAmount, setAreadyPaidAmount] = useState(0);
  const [updatedFormState, setUpdatedFormState] = useState([]);
  const [promoCodeDiscount, setPromoCodeDiscount] = useState(null);
  const [showDiscountButton, setShowDiscountButton] = useState(true);
  // Track if the total amount is zero due to discount (promo code or gift card)
  const [hasFullDiscount, setHasFullDiscount] = useState(false);
  const [isAddressSubmitted, setIsAddressSubmitted] = useState(false);

  // Gift Card States
  const [isGiftCardChecked, setIsGiftCardChecked] = useState(false);
  const [giftCardDiscount, setGiftCardDiscount] = useState(null);
  const [giftCardDiscountApplied, setGiftCardDiscountApplied] = useState(0);

  const [checkboxes, setCheckboxes] = useState({
    termsAndConditions: false,
    privacyPolicy: false,
    isConsentForMarketing: true,
    isGallerySharedPermission: true,
  });

  const isSBTipExtendCart =
    getItemFromStorage(localStorageKeys.SHUTTERB_TIP_EXTEND_CART) === TRUE;

  useEffect(() => {
    if (props.activeTab === eventBookingConstants.TABS_ID.CART) {
      window.scrollTo(0, 0);
      props.getMiscAmounts();
      setIsAddressSubmitted(false);
    }
    // eslint-disable-next-line
  }, [props.activeTab]);

  // extract only the required field from the edit events order object when editing events
  const editEventOrderDetails = useMemo(() => {
    const {
      selectedItemsTotal = 0,
      discountPercentage = 0,
      orderDiscountDetails = [],
    } = props.editEvents || {};

    const discountAmount = selectedItemsTotal * discountPercentage;
    const totalFeeWithoutDiscount = selectedItemsTotal - discountAmount;
    return {
      totalFeeWithDiscount: selectedItemsTotal,
      totalFeeWithoutDiscount,
      discountPercentage,
      orderDiscountDetails,
    };
    // eslint-disable-next-line
  }, [props.editEvents]);

  const miscAmounts = useMemo(() => {
    const base =
      (totalFee < 0 ? props.miscAmountsOld : props.miscAmounts) ?? {};
    const { tax: baseTax, ...rest } = base;

    return {
      ...rest,
      tax: props.isEditEvent
        ? props.miscAmountsOld?.tax || baseTax
        : isSBTipExtendCart
          ? props.myEventsExtendSb?.taxPercentage *
              numberConstants.ONE_HUNDRED ||
            props.miscAmountsOld?.tax ||
            baseTax
          : baseTax,
    };
  }, [
    totalFee,
    props.miscAmounts,
    props.miscAmountsOld,
    props.isEditEvent,
    isSBTipExtendCart,
  ]);

  const isExtendedCase = useMemo(() => {
    if (!props.formState || props.formState.length === 0) return false;

    return props.formState.every(
      (event) =>
        event.orderItems?.length > 0 &&
        event.orderItems.every(
          (item) => item.status === eventBookingStatus.EXTENDED
        )
    );
  }, [props.formState]);

  const isTipCase = useMemo(() => {
    if (!props.formState || props.formState.length === 0) return false;

    return props.formState.every(
      (event) =>
        event.orderItems?.length > 0 &&
        event.orderItems.every((item) => item.status === eventBookingStatus.TIP)
    );
  }, [props.formState]);

  // calculate the service fee whenever there is a change in the fee or tip
  useEffect(() => {
    if (props.activeTab === eventBookingConstants.TABS_ID.CART) {
      const serviceFee = calculatePercentage(
        totalFee + totalTip,
        miscAmounts?.serviceFee
      );

      setServiceFee(serviceFee);
    }
    // eslint-disable-next-line
  }, [totalFee, totalTip, miscAmounts]);

  // calculate tax whenever there is a change in the service fee
  useEffect(() => {
    const subTotal = totalFee
      ? totalFee +
        serviceFee -
        calculatePercentage(totalTip, miscAmounts?.serviceFee)
      : 0;
    const tax = calculatePercentage(subTotal, miscAmounts?.tax);
    setTaxPercentvalue(tax);
    // eslint-disable-next-line
  }, [serviceFee, miscAmounts]);

  useEffect(() => {
    if (isArrayWithItems(updatedFormState)) {
      const isEdited = updatedFormState?.every(
        (e) => e.status !== eventBookingStatus.CREATED
      );

      if (isEdited) {
        const { isConsentForMarketing, isGallerySharedPermission } =
          updatedFormState[0] || {};

        setCheckboxes((prev) => ({
          ...prev,
          termsAndConditions: true,
          privacyPolicy: true,
          isConsentForMarketing: isConsentForMarketing ?? false,
          isGallerySharedPermission: isGallerySharedPermission ?? false,
        }));
      }

      // runs when there is a change occurring in the events or recaps or apply promo code
      calculateTotalFee();
    }
    // eslint-disable-next-line
  }, [promoCodeDiscount, updatedFormState]);

  const calculateTotalFee = () => {
    let subtTotal = 0;
    let discountValue = 0;

    const isBalanceDue =
      totalFeeBackupRef.current > editEventOrderDetails?.totalFeeWithDiscount;
    setShowDiscountButton(isBalanceDue);

    if (isBalanceDue) {
      // only execute this if-else when there is promo code applied
      if (promoCodeDiscount?.type === CURRENCY_SYMBOL.DOLLAR) {
        let serviceFees = numberToDecimals(
          calculatePercentage(
            initialTotalFeeRef.current,
            miscAmounts?.serviceFee
          )
        );
        subtTotal = numberToDecimals(initialTotalFeeRef.current + +serviceFees);
        if (promoCodeDiscount?.value > subtTotal) {
          discountValue = initialTotalFeeRef.current;
        } else {
          discountValue = Number(promoCodeDiscount?.value);
        }
      } else if (promoCodeDiscount?.type === CURRENCY_SYMBOL.PERCENT) {
        discountValue =
          (initialTotalFeeRef.current * Number(promoCodeDiscount?.value)) / 100;
      }
    }
    setDiscount(discountValue);

    if (isBalanceDue && initialTotalFeeRef.current > -1) {
      const isDiscountExceed = discountValue >= initialTotalFeeRef.current;
      if (isDiscountExceed) {
        initialTotalFeeRef.current = 0;
        setHasFullDiscount(true);
      } else {
        initialTotalFeeRef.current = initialTotalFeeRef.current - discountValue;
        setHasFullDiscount(false);
      }
    }

    setTotalFee(initialTotalFeeRef.current);
  };

  // use to reset the gift card states (e.g, when apply the discount(promo code) reset the gift card if applied)
  const resetGiftCard = () => {
    setIsGiftCardChecked(false);
    setGiftCardDiscount(null);
    setGiftCardDiscountApplied(0);
  };

  const handleCheckboxChange = (key) => {
    setCheckboxes((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  useEffect(() => {
    if (props.url) {
      window.location.href = props.url;
    }
    // eslint-disable-next-line
  }, [props.url]);

  // Total amount that is caluclated by adding (fee + service fee + tax)
  // also subtract the gift card amount if applied
  const updatedTotal = useMemo(() => {
    const baseTotal = totalFee + serviceFee + taxPercentValue + totalTip;

    if (giftCardDiscount && giftCardDiscount.value) {
      const discountValue = giftCardDiscount.value ?? 0;

      if (baseTotal < 0) {
        resetGiftCard();
      } else if (baseTotal >= discountValue) {
        setRemainingBalance(0); // no leftover
        setGiftCardDiscountApplied(discountValue);
        setHasFullDiscount(false);

        return baseTotal - discountValue;
      } else {
        setRemainingBalance(discountValue - baseTotal); // leftover stored
        setGiftCardDiscountApplied(baseTotal); // only baseTotal applied
        setHasFullDiscount(true);

        return 0;
      }
    }

    setRemainingBalance(0); // reset if no gift card
    setGiftCardDiscountApplied(0); // reset applied gift card
    return baseTotal;
  }, [totalFee, serviceFee, taxPercentValue, totalTip, giftCardDiscount]);

  // checkout button click handler
  const handleCheckout = (isFromNewEvent = true) => {
    if (isFromNewEvent) {
      const isEditDraftEvent = Boolean(isDraftEvent && eventGroupId);
      const events = isEditDraftEvent
        ? filterSelectedShutterBsInEditDraftEvents(
            generateEvents(
              props.userId,
              checkboxes,
              true,
              isSBTipExtendCart,
              props.affiliateCode
            )
          )
        : generateEvents(
            props.userId,
            checkboxes,
            false,
            isSBTipExtendCart,
            props.affiliateCode,
            props.isEditEvent
          );
      let reels = [];

      if (props.isEditEvent) {
        // Compare the current recap with the backend data to set the status, ID and other required fields.
        const updatedRecapz = mergeRecapzWithBackup(
          props.addRecapz,
          props.addRecapzBackup
        );
        // In case of Edit events.
        // Update the recap format in the form to match the format required by the backend.
        reels = normalizeAddRecapz(
          updatedRecapz,
          props.editEvents?.reels || []
        );
      } else {
        // In case of Add, Draft, Tip/Extend Shutterb or Edit Draft events.
        // Update the recap format in the form to match the format required by the backend.
        reels = generateReels(props.addRecapz);
      }

      const firstEvent =
        Array.isArray(events) && events.length > 0 ? events[0] : null;

      const commonEventBookingFields = {
        billingDetails: props.billingDetails,
        serviceFee: parseFloat(numberToDecimals(serviceFee)),
        tax: parseFloat(numberToDecimals(taxPercentValue)),
        discount: parseFloat(numberToDecimals(discount)),
        // giftCardDiscount: parseFloat(numberToDecimals(giftCardDiscountApplied)),
        totalAmount: parseFloat(numberToDecimals(updatedTotal)),
        hasFullDiscount: hasFullDiscount,
        taxInPercentage: miscAmounts?.tax,
        orderDiscountDetails: [
          ...(promoCodeDiscount
            ? [
                {
                  ...promoCodeDiscount,
                  userId: props.userId,
                  amount: promoCodeDiscount?.value,
                  promoBalance: remainingBalance,
                  discountId: promoCodeDiscount?.id,
                  id: 0,
                },
              ]
            : []),
          ...(giftCardDiscount
            ? [
                {
                  ...giftCardDiscount,
                  userId: props.userId,
                  amount: giftCardDiscount?.value,
                  promoBalance: remainingBalance,
                  discountId: giftCardDiscount?.id,
                  id: 0,
                },
              ]
            : []),
        ],
        userId: props.userId,
        reels,
        events: events || [],
        eventGroupId: firstEvent?.eventGroupId || EMPTY_STRING,
        eventGroupName:
          firstEvent?.eventGroupName || firstEvent?.eventName || EMPTY_STRING,
      };

      const dataToSend = {
        order: isEditDraftEvent
          ? {
              ...props.draftEvents,
              ...commonEventBookingFields,
              checkoutStatus:
                eventBookingConstants.CHECKOUT_STATUS.EDIT_DRAFT_EVENT,
              isNewBooking: eventBookingConstants.IS_NEW_BOOKING.DRAFT,
            }
          : isSBTipExtendCart
            ? {
                ...commonEventBookingFields,
                id: updatedFormState[0]?.orderId,
                checkoutStatus:
                  eventBookingConstants.CHECKOUT_STATUS.TIP_OR_EXTEND,
              }
            : props.isEditEvent
              ? {
                  ...props.editEvents,
                  ...commonEventBookingFields,
                  isEditMode: true,
                  checkoutStatus: eventBookingConstants.CHECKOUT_STATUS.EDIT,
                  stripeCancelUrl: props.stripeCancelUrl,
                }
              : {
                  ...commonEventBookingFields,
                  isNewBooking: eventBookingConstants.IS_NEW_BOOKING.NEW_EVENT,
                },
      };

      const isValidationValid =
        isSBTipExtendCart ||
        validateEventBooking(dataToSend, props.isAddUpdateRecap);
      if (!isValidationValid) {
        return;
      }
      if (props.isAuthenticated) {
        props.checkOut(dataToSend);
      }
    }
  };

  const handlePrevious = () => {
    props.onPrevious();
  };

  return (
    <>
      <div className="cart-summary-container">
        <div className="row">
          <div className="col-xl-8 pl-lg-0">
            <CartSummaryTable
              updatedFormState={updatedFormState}
              setUpdatedFormState={setUpdatedFormState}
              setTotalFee={setTotalFee}
              initialTotalFeeRef={initialTotalFeeRef}
              setTotalTip={setTotalTip}
              activeTab={props.activeTab}
              totalFeeBackupRef={totalFeeBackupRef}
              setAreadyPaidAmount={setAreadyPaidAmount}
              editEventOrderDetails={editEventOrderDetails}
              isAddUpdateRecap={props.isAddUpdateRecap}
            />
          </div>
          <div className="col-xl-4 pl-lg-2 checkout-wrapper">
            <Checkout
              updatedFormState={updatedFormState}
              totalFee={totalFee}
              totalTip={totalTip}
              serviceFee={serviceFee}
              taxPercentValue={taxPercentValue}
              checkboxes={checkboxes}
              handleCheckboxChange={handleCheckboxChange}
              setIsModalOpen={setIsModalOpen}
              promoCodeDiscount={promoCodeDiscount}
              setPromoCodeDiscount={setPromoCodeDiscount}
              isGiftCardChecked={isGiftCardChecked}
              setIsGiftCardChecked={setIsGiftCardChecked}
              giftCardDiscount={giftCardDiscount}
              setGiftCardDiscount={setGiftCardDiscount}
              discount={discount}
              remainingBalance={remainingBalance}
              handleCheckout={handleCheckout}
              alreadyPaidAmount={alreadyPaidAmount}
              showDiscountButton={showDiscountButton}
              totalFeeBackupRef={totalFeeBackupRef}
              initialTotalFeeRef={initialTotalFeeRef}
              giftCardDiscountApplied={giftCardDiscountApplied}
              updatedTotal={updatedTotal}
              resetGiftCard={resetGiftCard}
              miscAmounts={miscAmounts}
              editEventOrderDetails={editEventOrderDetails}
              getTaxForLocation={props.getTaxForLocation}
              setIsAddressSubmitted={setIsAddressSubmitted}
              isAddressSubmitted={isAddressSubmitted}
              isAddUpdateRecap={props.isAddUpdateRecap}
              isExtendedCase={isExtendedCase}
              isTipCase={isTipCase}
            />
          </div>
        </div>
      </div>

      {!isSBTipExtendCart && (
        <BackButton
          className="event-back-button"
          onClick={handlePrevious}
          text={`${props.previousActiveTab}`}
        />
      )}

      {!props.isAuthenticated && isModalOpen && (
        <Modal
          showBackButton={false}
          formType={LOGIN_FORM}
          setIsModalOpen={setIsModalOpen}
          isAllowModal
        />
      )}
    </>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  url: state.eventBooking.url,
  userId: state.authentication.userId,
  addRecapz: state.eventBooking.addRecapz,
  editEvents: state.eventBooking.editEvents,
  miscAmounts: state.eventBooking.miscAmounts,
  draftEvents: state.eventBooking.draftEvents,
  isEditEvent: state.eventBooking.isEditEvent,
  miscAmountsOld: state.eventBooking.miscAmountsOld,
  addRecapzBackup: state.eventBooking.addRecapzBackup,
  isAuthenticated: state.authentication.isAuthenticated,
  previousActiveTab: state.eventBooking.previousActiveTab,
  billingDetails: state.eventBooking.billingDetails,
  formState: state.eventBooking.formState,
  myEventsExtendSb: state.customerDashboard.myEventsExtendSb,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  checkOut: (data) => dispatch(eventBookingActions.checkOut(data)),
  getMiscAmounts: () => dispatch(eventBookingActions.getMiscAmounts()),
  getTaxForLocation: (locationInfo, onSuccess) =>
    dispatch(eventBookingActions.getTaxForLocation(locationInfo, onSuccess)),
});

// mapping action and store the function via props
export default connect(mapStateToProps, mapDispatchToProps)(CartSummary);
